import { exists } from '@jack-henry/frontend-utils/functions';
import { LookupModelDto } from '@treasury/api/channel';
import { nullLookupModelDto } from '@treasury/domain/wires/wire.dto';
import { css, html, nothing, PropertyValueMap } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { TmBaseComponent } from '..';
import { deleteIcon, plusIcon } from '../assets/icons';
import './tm-button';
import './tm-text-field';

export const tagName = 'tm-multi-line-text-input';
@customElement(tagName)
export class TmMultiLineTextInput extends TmBaseComponent {
    @property()
    public label?: string;

    @property({ type: Array })
    public value: LookupModelDto[] = [{ ...nullLookupModelDto }];

    @property({ type: Boolean })
    public required = false;

    @property({ type: Boolean })
    public disabled = false;

    @property({ type: Number })
    public maxLines?: number;

    @property({ type: Number })
    public minLines = 1;

    @property({ type: String })
    public tooltip?: string;

    @property({ type: String })
    public placeholder?: string;

    @property({ type: Number, reflect: true })
    public characterLimit?: number;

    @property({ type: String, reflect: true })
    public pattern?: string;

    @property({ type: Boolean })
    public formRowSlotted = false;

    protected firstUpdated(): void {
        if (exists(this.maxLines) && Number(this.minLines) > Number(this.maxLines)) {
            throw new Error(
                'Minimum lines must be less than maximum lines for multi-line text input'
            );
        }

        if (this.value.length < Number(this.minLines)) {
            while (this.value.length < Number(this.minLines)) {
                this.value.push({ ...nullLookupModelDto });
            }
            this.requestUpdate();
        }
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    protected updated(changedProperties: PropertyValueMap<any>): void {
        if (changedProperties.has('value')) {
            this.dispatchEvent(new CustomEvent('change', { detail: this.value }));
        }
    }

    protected render() {
        return html`
            ${this.value.map(
                (item, i) => html`
                    <div
                        class="tm-multi-line-item flex relative items-baseline w-full m-0"
                        ?disabled=${this.disabled}
                    >
                        ${this.renderItem(item, i)}
                    </div>
                `
            )}
            ${this.renderFormRowActions()}
        `;
    }

    private renderItemAddBtn(index: number) {
        if (this.maxLines && this.value.length === Number(this.maxLines)) {
            return nothing;
        }
        if (index < this.value.length - 1) {
            return nothing;
        }

        return html`
            <tm-button
                id="item-add-btn"
                @click=${() => {
                    this.value.push({ ...nullLookupModelDto });
                    this.requestUpdate('value');
                }}
                isIcon
            >
                ${plusIcon}
            </tm-button>
        `;
    }

    private renderItemDelBtn(index: number) {
        if (this.value.length <= this.minLines) {
            return nothing;
        }

        return html`
            <tm-button
                id="item-delete-btn"
                @click=${() => {
                    this.value.splice(index, 1);
                    this.requestUpdate('value');
                }}
                isIcon
            >
                ${deleteIcon}
            </tm-button>
        `;
    }

    private renderItemActions(index: number) {
        if (this.formRowSlotted) {
            return nothing;
        }
        return [this.renderItemAddBtn(index), this.renderItemDelBtn(index)];
    }

    private renderFormRowActions() {
        if (!this.formRowSlotted) {
            return nothing;
        }
        return html`<div class="form-row-actions flex justify-between">
            ${this.renderFormRowAddBtn()} ${this.renderFormRowDelBtn()}
        </div>`;
    }

    private renderFormRowAddBtn() {
        if (this.maxLines && this.value.length === Number(this.maxLines)) {
            return nothing;
        }
        return html`<button
            id="form-row-add-btn"
            @click=${() => {
                this.value.push({ ...nullLookupModelDto });
                this.requestUpdate('value');
            }}
        >
            ${plusIcon} Add Line
        </button>`;
    }

    private renderFormRowDelBtn() {
        if (this.value.length <= this.minLines) {
            return nothing;
        }
        return html`<button
            id="form-row-delete-btn"
            @click=${() => {
                this.value.pop();
                this.requestUpdate('value');
            }}
        >
            ${deleteIcon} Remove Line
        </button>`;
    }

    private renderItem(item: LookupModelDto, index: number) {
        const {
            label = '',
            required = false,
            tooltip = undefined,
        } = index === 0
            ? { label: this.label, required: this.required, tooltip: this.tooltip }
            : {};

        const placeholder = this.placeholder ? `${this.placeholder} ${index + 1}` : '';
        const helperText = `${item.value?.length || 0}/${this.characterLimit}`;
        const initial = item.value ?? '';

        return html`
            <tm-text-field
                class=${classMap({
                    'mb-0': true,
                    'flex-auto': true,
                    'form-row-slotted': this.formRowSlotted,
                    'helper-align-right': this.formRowSlotted && !!this.characterLimit,
                })}
                .label=${label}
                .placeholder=${placeholder}
                .required=${required}
                .disabled=${this.disabled}
                .initial=${initial}
                .maxLength=${this.characterLimit}
                .pattern=${this.pattern}
                @value-changed=${(e: CustomEvent) => {
                    if (this.value[index]) {
                        this.value[index].value = e.detail.value;
                    }
                    this.requestUpdate('value');
                }}
                .tooltip=${tooltip}
                .helperText=${helperText}
                filled
            ></tm-text-field>
            <div class="action-buttons">${this.renderItemActions(index)}</div>
        `;
    }

    public static get styles() {
        return [
            css`
                :host {
                    margin-bottom: 16px;
                }

                .tm-multi-line-item[disabled] .action-buttons {
                    display: none;
                }

                .tm-multi-line-item tm-button:first-of-type {
                    margin-left: 4px;
                }

                .form-row-actions {
                    margin-top: 8px;
                }

                .form-row-actions button {
                    display: flex;
                    color: var(--content-action);
                }

                .form-row-actions button svg {
                    height: 16px;
                    margin-top: 2px;
                }

                .form-row-actions button path {
                    fill: var(--content-action);
                }

                .form-row-actions button:first-of-type {
                    margin-right: 16px;
                }
            `,
        ];
    }
}

declare global {
    interface HTMLElementTagNameMap {
        [tagName]: TmMultiLineTextInput;
    }
}
